import type { MeResponse, UserRole } from '../lib/api/types.js';

/**
 * A development-only way into the application, and nothing more.
 *
 * Google OAuth is untouched — `apps/api` is still the only authority on who
 * is signed in, every learner route is still behind SessionGuard and every
 * /api/admin/* route is still 403 for a learner (FR-027, FR-061). What this
 * does is let the Web app's OWN route boundaries resolve without a Google
 * round trip, so every screen and flow can be opened and reviewed while the
 * platform is being built.
 *
 * It is therefore a way to SEE the screens, not a way to get data: the server
 * still answers 401 to a browser holding no real session, and screens whose
 * content comes from the API will show their empty and error states. When a
 * real Google session IS present, it is used as it always was and the choice
 * here only overrides the role — which is what makes the administrator
 * screens reachable before anyone has run `admin:grant`.
 *
 * `import.meta.env.DEV` is false in every production build, so Vite drops
 * both the branch and this module's identities from the bundle. The choice is
 * kept in `localStorage` so a reload lands back on the same screen as the same
 * person, rather than at sign-in.
 */

const STORAGE_KEY = 'cavaquinho.dev-role';

export const DEV_AUTH_AVAILABLE: boolean = import.meta.env.DEV;

export const DEV_ROLES: readonly UserRole[] = ['learner', 'admin'];

/** How each role is named on the buttons that choose it. */
export const DEV_ROLE_LABELS: Record<UserRole, string> = {
  learner: 'Aluno',
  admin: 'Administrador',
};

/** The chosen role, or null when the normal Google flow is in force. */
export function devRole(): UserRole | null {
  if (!DEV_AUTH_AVAILABLE) return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && (DEV_ROLES as readonly string[]).includes(stored)
      ? (stored as UserRole)
      : null;
  } catch {
    // Storage can be unavailable (private windows, blocked site data). That
    // is not a reason to fail — it just means no choice is remembered.
    return null;
  }
}

export function setDevRole(role: UserRole | null): void {
  if (!DEV_AUTH_AVAILABLE) return;
  try {
    if (role === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, role);
  } catch {
    /* see devRole() */
  }
}

/**
 * The stand-in account used when the API has no session for this browser.
 *
 * The id is a real uuid so anything that echoes it looks like what it will
 * look like in earnest, and the address says plainly where the account came
 * from — nothing here ever reaches the database.
 */
export function devUser(role: UserRole): MeResponse {
  return {
    id:
      role === 'admin'
        ? '00000000-0000-4000-8000-00000000ad11'
        : '00000000-0000-4000-8000-000000000a10',
    name: DEV_ROLE_LABELS[role],
    email: `${role}@dev.local`,
    image: null,
    // The browser's own zone, so a stand-in account counts its days where the
    // developer is sitting rather than in UTC — the streak on screen is then
    // the streak a real account would show.
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    role,
    locale: 'pt-BR',
    profileVisibility: 'public',
  };
}
